import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp, FiSearch } from "react-icons/fi";
import CustomTooltip from "./CustomTooltip";
import "./ProductCodeSelect.css";

const normalizeText = (v) => String(v ?? "").trim();

const ProductCodeSelect = ({
  value,
  onChange,
  options = [],
  width = "100%",
  placeholder = "Select Product",
  innerPlaceholder="Search by code, description or type",
  disabled = false,
  searchThreshold = 5,
  customerName,
  noDataText ,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [canScroll, setCanScroll] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(360);
  const containerRef = useRef(null);
  const optionsRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      options.map((opt) => {
        const productCode = normalizeText(opt.productCode || opt.value);
        const productDescription = normalizeText(opt.productDescription || "-");
        const productType = normalizeText(opt.productType || "-");
        const searchText = normalizeText(
          opt.searchText || `${productCode} ${productDescription} ${productType}`
        ).toLowerCase();
        const ignoreSearch = opt.ignoreSearch!==undefined ? opt.ignoreSearch :false;
        const ignoreDescription = opt.ignoreDescription!==undefined ? opt.ignoreDescription :false;

        return {
          ...opt,
          value: normalizeText(opt.value || productCode),
          productCode,
          productDescription,
          productType,
          searchText,
          ignoreDescription,
          ignoreSearch,
        };
      }),
    [options]
  );

  const selected = normalizedOptions.find((o) => o.value === value);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return normalizedOptions;
    return normalizedOptions
      .filter( o => !o.ignoreSearch)
      .filter((o) => o.searchText.includes(term));
  }, [normalizedOptions, searchTerm]);

  const hasSearch = normalizedOptions.length > searchThreshold;
  const optionsMaxHeight = Math.max(
    120,
    dropdownMaxHeight - (hasSearch ? 56 : 0) - (canScroll ? 32 : 0)
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = optionsRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const scrollable = el.scrollHeight > el.clientHeight + 2;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      setCanScroll(scrollable);
      setIsAtBottom(atBottom);
    };

    updateScrollState();
    const raf = window.requestAnimationFrame(updateScrollState);
    return () => window.cancelAnimationFrame(raf);
  }, [open, filteredOptions.length]);

  useEffect(() => {
    if (!open) return;

    const updateDropdownHeight = () => {
      const host = containerRef.current;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      const viewportGap = 12; // keep small gap from screen bottom
      const availableBelow = window.innerHeight - rect.bottom - viewportGap;
      const bounded = Math.max(180, Math.min(420, availableBelow));
      setDropdownMaxHeight(bounded);
    };

    updateDropdownHeight();
    window.addEventListener("resize", updateDropdownHeight);
    window.addEventListener("scroll", updateDropdownHeight, true);
    return () => {
      window.removeEventListener("resize", updateDropdownHeight);
      window.removeEventListener("scroll", updateDropdownHeight, true);
    };
  }, [open]);

  return (
    <div className="product-select" style={{ width }} ref={containerRef}>
      <button
        type="button"
        className={`product-select-trigger ${disabled ? "is-disabled" : ""}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <div className="product-select-trigger-content">
          {selected ? (
            <CustomTooltip message={selected.productCode}>
              <p className="product-select-code">{selected.productCode}</p>
            </CustomTooltip>
          ) : (
            <span className="product-select-placeholder">{placeholder}</span>
          )}
        </div>
        <span className="product-select-arrow">
          {open ? <FiChevronUp /> : <FiChevronDown />}
        </span>
      </button>

      {open && !disabled && (
        <div
          className="product-select-dropdown"
          style={{ maxHeight: `${dropdownMaxHeight}px` }}
        >
          {hasSearch && (
            <div className="product-select-search">
              <FiSearch className="product-select-search-icon" />
              <input
                type="text"
                placeholder={innerPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="product-select-search-input"
                autoFocus
              />
            </div>
          )}

          <div
            className="product-select-options"
            ref={optionsRef}
            style={{ maxHeight: `${optionsMaxHeight}px` }}
            onScroll={() => {
              const el = optionsRef.current;
              if (!el) return;
              const atBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
              setIsAtBottom(atBottom);
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt,index) => (
                <button
                  type="button"
                  key={index}
                  className={`product-select-option ${
                    opt.value === value ? "is-selected" : ""
                  } ${opt.disabled ? "is-option-disabled" : ""}`}
                  disabled={opt.disabled}
                  onClick={() => {
                    if (opt.disabled) return;
                    onChange?.(opt.value);
                    setOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <div className="product-select-option-top">
                    <CustomTooltip message={opt.productCode}>
                      <p className="product-select-option-code">{opt.productCode}</p>
                    </CustomTooltip>
                  </div>
                  {
                    !opt.ignoreDescription 
                    && 
                    <CustomTooltip message={opt.productDescription}>
                      <p className="product-select-option-description">
                        {opt.productDescription}
                      </p>
                    </CustomTooltip>
                  }
                  
                </button>
              ))
            ) : (
                <p className="product-select-no-data">
                  {noDataText ||
                    (customerName
                      ? `No products mapped to ${customerName}`
                      : "No products mapped to this customer")}
                </p>
            )}
          </div>

          {canScroll && (
            <div className="product-select-scroll-status">
              {isAtBottom ? "End of results" : "Scroll down for more"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCodeSelect;
